import { supabase } from '../../config/supabase';
import { uuidToShortId } from '../common/shortId';

/**
 * Send SMS via Twilio
 * @param {string} to - Phone number (with country code, e.g., +61412345678)
 * @param {string} message - SMS message content
 * @param {string} companyId - Company ID (required)
 * @param {string} userId - Optional user ID for logging
 * @param {string} jobId - Optional job ID for logging
 * @returns {Promise<Object>} Result of SMS send
 */
export const sendSMS = async (to, message, companyId, userId = null, jobId = null) => {
    try {
        if (!companyId) {
            throw new Error('Company ID is required');
        }

        const { data, error } = await supabase.functions.invoke('send-sms', {
            body: {
                to,
                message,
                companyId,
                userId,
                jobId
            }
        });

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error sending SMS:', error);
        return { data: null, error };
    }
};


/**
 * Send SMS via Twilio
 * @param {string} to - Phone number (with country code, e.g., +61412345678)
 * @param {string} message - SMS message content
 * @param {string} companyId - Company ID (required)
 * @param {string} userId - Optional user ID for logging
 * @param {string} jobId - Optional job ID for logging
 * @returns {Promise<Object>} Result of SMS send
 * 
 *  assignment,
                company,
                driverId,
                selectedQuoteTypeForDriver
 */
export const sendDriverSMS = async (assignment, company, driverId, quoteType, deliveryId) => {
    try {
        if (!company) {
            throw new Error('Company is required');
        }

        if (!deliveryId) {
            throw new Error('Delivery ID is required for driver SMS');
        }

        const shortDeliveryId = uuidToShortId(deliveryId);

        const deliveryUrl = `${process.env.REACT_APP_APP_URL}/${company.slug}/assignment/${shortDeliveryId}`;

        const { data: driverData, error: driverError } = await supabase
            .from('users')
            .select(`*`)
            .eq('id', driverId)
            .eq('company_id', company.id)
            .single();

        if (driverError) throw driverError;

        if (!driverData?.phone || driverData?.phone?.length <= 0) {
            return console.log("Driver not notified, no phone number");
        }


        const { data, error } = await supabase.functions.invoke('sendDriverSMS', {
            body: {
                to: driverData.phone,
                message: `Hello ${driverData.full_name || driverData.username}. New delivery assigned to you: ${assignment.unique_id} (${quoteType}).\n${deliveryUrl}`,
                companyId: company.id,
                jobId: assignment.id,
                userId: driverId,
                driverName: driverData.full_name || driverData.username,
                companyName: company.name,
                trackingId: shortDeliveryId
            }
        });

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error sending SMS:', error);
        return { data: null, error };
    }
};
/**
 * Send job assignment notification
 */
// export const sendJobAssignmentSMS = async (phoneNumber, userName, jobId, jobType, companyId) => {
//     const message = `Hi ${userName}, you've been assigned to job ${jobId} (${jobType}). Please check your dashboard for details.`;
//     return await sendSMS(phoneNumber, message, companyId, null, jobId);
// };

const formatDeliveryDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short'
    });
};

/**
 * Send delivery notification
 */
export const sendInTransitNotificationSMS = async (phoneNumber, customerName, shortId, jobId, deliveryDate, company, jobName) => {

    if (!phoneNumber || phoneNumber?.length <= 0) return;
    const formattedDate = formatDeliveryDate(deliveryDate);

    const message = `Hi ${customerName}, your delivery for job ${jobName} is on its way. Tracking code: ${shortId}. Track: ${process.env.REACT_APP_APP_URL}/track/${shortId} – ${company.name}`;
    return await sendSMS(phoneNumber, message, company.id, null, jobId);
};

export const sendDeliveredNotificationSMS = async (phoneNumber, customerName, shortId, jobId, deliveryDate, company, jobName) => {

    if (!phoneNumber || phoneNumber?.length <= 0) return;
    const formattedDate = formatDeliveryDate(deliveryDate);

    const message = `Hi ${customerName}, your delivery for job ${jobName} has been delivered. Delivered on: ${formattedDate}. Tracking code: ${shortId}. Details: ${process.env.REACT_APP_APP_URL}/track/${shortId} – ${company.name}`;
    return await sendSMS(phoneNumber, message, company.id, null, jobId);
};

/**
 * Send urgent job notification
 */
// export const sendUrgentJobSMS = async (phoneNumber, userName, jobId, companyId) => {
//     const message = `URGENT: ${userName}, job ${jobId} requires immediate attention. Please check the system ASAP.`;
//     return await sendSMS(phoneNumber, message, companyId, null, jobId);
// };

/**
 * Get SMS logs for a company
 * @param {string} companyId - Company ID
 * @param {number} limit - Number of logs to retrieve
 * @returns {Promise<Object>} SMS logs
 */
export const getSMSLogs = async (companyId, limit = 50) => {
    try {
        const { data, error } = await supabase
            .from('sms_logs')
            .select('*')
            .eq('company_id', companyId)
            .order('sent_at', { ascending: false })
            .limit(limit);

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error fetching SMS logs:', error);
        return { data: null, error };
    }
};